// Papéis: nome (PT+EN), posição na hierarquia, permissões e limite de compensação de cada papel da equipa.
// Só aparece a quem tem "roles.manage". Quem não é admin só mexe nos papéis abaixo do seu e não dá o que não tem.
// As mesmas regras (checkRole*) correm no backend; aqui servem para desativar o que não se pode fazer e explicar porquê.
import { useApi, useMe, useRefreshMe } from "@ecliptica/api-client/react";
import {
  canManageRole,
  checkRoleDelete,
  checkRoleMove,
  isAdmin,
  normalizePermissions,
  PERMISSIONS,
  ROLE_KEY_RE,
  type Permission,
  type RoleDef,
  type Roles,
} from "@ecliptica/contexts-accounts";
import { contentLangs, filledInActiveLangs, useTranslation, type Lang } from "@ecliptica/i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageHeader,
  Skeleton,
  toast,
} from "@ecliptica/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useCan } from "../../features/auth/RequireStaff";
import { ROLES_KEY, useRoleLabel, useRoles } from "../../features/roles/useRoles";

/** "users.read" → "users_read" (o i18next usa "." para aninhar chaves). */
const permKey = (p: string) => p.replace(".", "_");

/** Rótulo do campo do nome em cada língua (o nome de um papel é conteúdo: só se editam as línguas ativas). */
const LABEL_KEY = { pt: "roles.labelPt", en: "roles.labelEn" } as const satisfies Record<Lang, string>;

type Draft = { labels: Record<Lang, string>; permissions: Permission[]; limit: string; unlimited: boolean };
const toDraft = (r: RoleDef): Draft => ({
  labels: { ...r.labels },
  permissions: [...r.permissions],
  limit: r.compensationLimit === null ? "" : String(r.compensationLimit),
  unlimited: r.compensationLimit === null,
});

export function RolesPage() {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const me = useMe();
  const refreshMe = useRefreshMe();
  const roles = useRoles();
  const label = useRoleLabel();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const can = useCan();

  if (!can("roles.manage")) return <Alert variant="warning">{t("roles.errors.forbidden")}</Alert>;
  if (!roles.data || !me.data) return <Skeleton className="h-96 w-full max-w-5xl" />;
  const set = roles.data.set;
  const actor = { role: me.data.user.role };
  const counts = Object.fromEntries(roles.data.roles.map((r) => [r.key, r.users]));
  // Do topo (admin) para a base (player), como se lê uma hierarquia.
  const top = [...set.list].reverse();
  const current = set.get(selected) ?? top[0]!;

  async function afterWrite(res: Response | { ok: boolean; json: () => Promise<unknown> }, okMsg: string) {
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      const key = `roles.errors.${body.error ?? ""}`;
      toast.error(body.error && t(key) !== key ? t(key) : t("error.generic"));
      return false;
    }
    toast.success(okMsg);
    await qc.invalidateQueries({ queryKey: ROLES_KEY });
    await qc.invalidateQueries({ queryKey: ["audit"] });
    await refreshMe(); // as minhas permissões podem ter mudado
    return true;
  }

  async function move(key: string, direction: "up" | "down") {
    setBusy(true);
    await afterWrite(await api.v1.admin.roles[":key"].move.$post({ param: { key }, json: { direction } }), t("roles.moved"));
    setBusy(false);
  }

  return (
    <>
      <PageHeader
        title={t("nav.roles")}
        description={t("roles.description")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> {t("roles.new")}
          </Button>
        }
      />
      <div className="grid max-w-6xl gap-5 lg:grid-cols-[22rem_1fr]">
        <Card className="h-fit">
          <p className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("roles.hierarchy")}</p>
          <ul className="divide-y">
            {top.map((r) => {
              const up = checkRoleMove(set, actor, r.key, "up");
              const down = checkRoleMove(set, actor, r.key, "down");
              const manageable = canManageRole(set, actor, r.key).ok;
              return (
                <li key={r.key} className={cn("flex items-center gap-2 px-3 py-2", r.key === current.key && "bg-accent")}>
                  <button type="button" className="flex min-w-0 flex-1 cursor-pointer flex-col items-start text-left" onClick={() => setSelected(r.key)}>
                    <span className="flex items-center gap-1.5 font-semibold">
                      {label(r.key, r.labels)}
                      {!manageable && <Lock className="size-3 text-muted-foreground" aria-label={t("roles.locked")} />}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {r.key} · {t("roles.users", { count: counts[r.key] ?? 0 })}
                    </span>
                  </button>
                  {r.builtIn ? (
                    <Badge variant="secondary">{t("roles.builtIn")}</Badge>
                  ) : (
                    <span className="flex gap-1">
                      <Button size="icon" variant="ghost" aria-label={t("roles.up")} title={up.ok ? t("roles.up") : t(`roles.errors.${up.reason}`)} disabled={busy || !up.ok} onClick={() => void move(r.key, "up")}>
                        <ArrowUp />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t("roles.down")}
                        title={down.ok ? t("roles.down") : t(`roles.errors.${down.reason}`)}
                        disabled={busy || !down.ok}
                        onClick={() => void move(r.key, "down")}
                      >
                        <ArrowDown />
                      </Button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
        <RoleEditor key={JSON.stringify(current)} role={current} roles={set} actorRole={actor.role} users={counts[current.key] ?? 0} onWrite={afterWrite} onDeleted={() => setSelected(null)} />
      </div>
      <CreateRoleDialog
        open={creating}
        onOpenChange={setCreating}
        onCreate={async (key, labels) => {
          const ok = await afterWrite(await api.v1.admin.roles.$post({ json: { key, labels, permissions: [], compensationLimit: 0 } }), t("roles.created"));
          if (ok) setSelected(key);
          return ok;
        }}
      />
    </>
  );
}

function RoleEditor({
  role,
  roles,
  actorRole,
  users,
  onWrite,
  onDeleted,
}: {
  role: RoleDef;
  roles: Roles;
  actorRole: string;
  users: number;
  onWrite: (res: Response | { ok: boolean; json: () => Promise<unknown> }, okMsg: string) => Promise<boolean>;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const label = useRoleLabel();
  const [draft, setDraft] = useState<Draft>(() => toDraft(role));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const actor = { role: actorRole };
  const manage = canManageRole(roles, actor, role.key);
  const editable = manage.ok;
  const admin = isAdmin(actorRole);
  const mine = new Set(roles.get(actorRole)?.permissions ?? []);
  const del = checkRoleDelete(roles, actor, role.key, users);
  const fixedPerms = role.builtIn; // admin: todas; player: nenhuma
  const perms = normalizePermissions(draft.permissions);
  const limit = draft.unlimited ? null : Math.max(0, Math.floor(Number(draft.limit) || 0));
  const changed =
    JSON.stringify(draft.labels) !== JSON.stringify(role.labels) || perms.join() !== role.permissions.join() || limit !== role.compensationLimit;

  function toggle(p: Permission, on: boolean) {
    setDraft((d) => ({ ...d, permissions: on ? [...d.permissions, p] : d.permissions.filter((x) => x !== p) }));
  }

  async function save() {
    setBusy(true);
    const ok = await onWrite(
      await api.v1.admin.roles[":key"].$put({ param: { key: role.key }, json: { labels: draft.labels, permissions: perms, compensationLimit: limit, reason: reason || undefined } }),
      t("roles.saved"),
    );
    setBusy(false);
    if (ok) setReason("");
  }

  return (
    <Card className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-extrabold text-gold">{label(role.key, role.labels)}</h2>
        <span className="font-mono text-xs text-muted-foreground">{role.key}</span>
        {role.builtIn && <Badge variant="secondary">{t("roles.builtIn")}</Badge>}
        {changed && <Badge variant="warning">{t("roles.unsaved")}</Badge>}
      </div>
      {role.key === "admin" && <Alert variant="info">{t("roles.adminNote")}</Alert>}
      {role.key === "player" && <Alert variant="info">{t("roles.playerNote")}</Alert>}
      {!editable && <Alert variant="warning">{t(`roles.errors.${manage.ok ? "forbidden" : manage.reason}`)}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2">
        {contentLangs().map((l) => (
          <div key={l} className="flex flex-col gap-1.5">
            <Label htmlFor={`role-label-${l}`}>{t(LABEL_KEY[l])}</Label>
            <Input
              id={`role-label-${l}`}
              maxLength={40}
              disabled={!editable || busy}
              value={draft.labels[l]}
              onChange={(e) => setDraft((d) => ({ ...d, labels: { ...d.labels, [l]: e.target.value } }))}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="role-limit">{t("roles.limit")}</Label>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            id="role-limit"
            type="number"
            min={0}
            max={1_000_000}
            className="w-40"
            disabled={!editable || busy || fixedPerms || draft.unlimited}
            value={draft.unlimited ? "" : draft.limit}
            onChange={(e) => setDraft((d) => ({ ...d, limit: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-gold"
              disabled={!editable || busy || fixedPerms}
              checked={draft.unlimited}
              onChange={(e) => setDraft((d) => ({ ...d, unlimited: e.target.checked }))}
            />
            {t("roles.unlimited")}
          </label>
        </div>
        <p className="text-xs text-muted-foreground">{t("roles.limitHint")}</p>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t("roles.permissions")}</h3>
        <ul className="grid gap-2 sm:grid-cols-2">
          {PERMISSIONS.map((p) => {
            const on = perms.includes(p);
            // Não se dá o que não se tem (admin dá tudo); tirar é sempre possível. Com outras permissões, o acesso ao backoffice é obrigatório.
            const notHeld = !admin && !mine.has(p) && !role.permissions.includes(p);
            const forced = p === "backoffice.access" && perms.some((x) => x !== "backoffice.access");
            const disabled = !editable || busy || fixedPerms || forced || (notHeld && !on);
            return (
              <li key={p}>
                <label className={cn("flex h-full cursor-pointer items-start gap-3 rounded-md border p-3", disabled && "cursor-not-allowed opacity-70", on && "border-gold/50")}>
                  <input type="checkbox" className="mt-0.5 size-4 accent-gold" checked={on} disabled={disabled} onChange={(e) => toggle(p, e.target.checked)} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{t(`roles.perm.${permKey(p)}.label`)}</span>
                    <span className="block text-xs text-muted-foreground">{t(`roles.perm.${permKey(p)}.hint`)}</span>
                    <span className="mt-1 block font-mono text-[10px] text-muted-foreground">{p}</span>
                    {notHeld && !on && editable && !fixedPerms && <span className="mt-1 block text-xs text-warning">{t("roles.notHeld")}</span>}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {editable && (
        <div className="flex flex-col gap-3 border-t pt-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="role-reason">{t("roles.reason")}</Label>
            <Input id="role-reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled={!changed || busy || !filledInActiveLangs(draft.labels)} onClick={() => void save()}>
              {t("action.save")}
            </Button>
            <Button variant="outline" disabled={!changed || busy} onClick={() => setDraft(toDraft(role))}>
              {t("roles.discard")}
            </Button>
            <span className="flex-1" />
            <Button variant="destructive" disabled={!del.ok || busy} title={del.ok ? undefined : t(`roles.errors.${del.reason}`)} onClick={() => setConfirmDelete(true)}>
              <Trash2 /> {t("roles.delete")}
            </Button>
          </div>
          {!del.ok && del.reason === "in_use" && <p className="text-xs text-muted-foreground">{t("roles.errors.in_use")}</p>}
        </div>
      )}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("roles.deleteTitle", { name: label(role.key, role.labels) })}</DialogTitle>
            <DialogDescription>{t("roles.deleteHint")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t("action.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const ok = await onWrite(await api.v1.admin.roles[":key"].$delete({ param: { key: role.key }, json: { reason: reason || undefined } }), t("roles.deleted"));
                setBusy(false);
                setConfirmDelete(false);
                if (ok) onDeleted();
              }}
            >
              {t("roles.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CreateRoleDialog({ open, onOpenChange, onCreate }: { open: boolean; onOpenChange: (o: boolean) => void; onCreate: (key: string, labels: Partial<Record<Lang, string>>) => Promise<boolean> }) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  // Papel novo: só as línguas ativas (as inativas ficam vazias e podem preencher-se quando voltarem a estar ativas).
  const [labels, setLabels] = useState<Partial<Record<Lang, string>>>({});
  const [busy, setBusy] = useState(false);
  const validKey = ROLE_KEY_RE.test(key);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("roles.new")}</DialogTitle>
          <DialogDescription>{t("roles.newHint")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-role-key">{t("roles.key")}</Label>
          <Input id="new-role-key" maxLength={32} value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} className="font-mono" />
          <p className={cn("text-xs", key && !validKey ? "text-destructive" : "text-muted-foreground")}>{t("roles.keyHint")}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {contentLangs().map((l) => (
            <div key={l} className="flex flex-col gap-1.5">
              <Label htmlFor={`new-role-${l}`}>{t(LABEL_KEY[l])}</Label>
              <Input id={`new-role-${l}`} maxLength={40} value={labels[l] ?? ""} onChange={(e) => setLabels((x) => ({ ...x, [l]: e.target.value }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("action.cancel")}
          </Button>
          <Button
            disabled={busy || !validKey || !filledInActiveLangs(labels)}
            onClick={async () => {
              setBusy(true);
              const ok = await onCreate(key, labels);
              setBusy(false);
              if (ok) {
                setKey("");
                setLabels({});
                onOpenChange(false);
              }
            }}
          >
            {t("roles.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
